import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'

/**
 * A small centred dialog for the two or three fields a record needs to exist.
 *
 * Not a slide-over: a panel is for reading something that already exists beside
 * the list it came from, and there is nothing here to keep your place in.
 *
 * Escape closes it, and so does the backdrop — a form with three fields is not
 * worth trapping someone in.
 */
export function Dialog({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useTranslation()

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', close)

    return () => window.removeEventListener('keydown', close)
  }, [onClose])

  return (
    <>
      <button
        type="button"
        aria-label={t('view.close')}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/25"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="border-line bg-surface fixed inset-x-4 top-24 z-50 mx-auto max-w-lg rounded-lg border shadow-2xl"
      >
        <header className="border-line flex items-center gap-3 border-b px-5 py-3.5">
          <h2 className="text-ink-900 min-w-0 flex-1 text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            title={t('view.close')}
            aria-label={t('view.close')}
            className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
          >
            <Icon name="close" className="size-4" />
          </button>
        </header>

        {children}
      </div>
    </>
  )
}

/** One labelled control. Every field in these forms is the same shape. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">{label}</span>
      {children}
    </label>
  )
}

export const FIELD_CLASS =
  'border-line bg-surface text-ink-900 w-full rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50'
